import React from "react"
import PropTypes from "prop-types"
import SanityImage from "gatsby-plugin-sanity-image"
import Link from "components/Link"
import useMediaQuery from "hooks/useMediaQuery"
import theme from "styles/theme"

import { ReactComponent as RightArrow } from "images/icons/arrow.svg"

const colors = {
  primary: {
    color: theme.textGray,
    background: theme.blue20,
    "--accentColor": theme.midBlue,
    "--labelColor": theme.lightBlue,
    ":hover": {
      color: theme.white,
      background: theme.midBlue,
      "--accentColor": theme.white,
      "--labelColor": theme.white,
    },
  },

  secondary: {
    color: theme.textGray,
    background: theme.lightYellow,
    "--accentColor": theme.orange,
    "--labelColor": theme.midOrange,
    ":hover": {
      color: theme.white,
      background: theme.orange,
      "--accentColor": theme.white,
      "--labelColor": theme.white,
    },
  },
}

const ReadMoreButton = () => (
  <div
    css={{
      display: "none",
      [theme.mobilePortrait]: {
        display: "inline-flex",
        alignItems: "center",
        color: "var(--accentColor)",
        borderBottom: "1px solid currentColor",
        fontSize: 13,
        fontWeight: 500,
        marginTop: 30,
        paddingBottom: 5,
      },
    }}
  >
    Read More
    <RightArrow
      css={{
        width: 15,
        marginLeft: 6,
      }}
    />
  </div>
)

const ExploreMoreItem = ({ image, label, title, description, link, media }) => {
  return (
    <Link
      href={link}
      css={{
        display: "grid",
        gridTemplateColumns: "25% 1fr",
        transition: "background 250ms, color 250ms",
        ...colors.secondary,
        "@media (min-width: 961px)": {
          ":nth-of-type(3n+1)": colors.primary,
        },
        [theme.tabletSmall]: {
          ":nth-of-type(odd)": colors.primary,
          gridTemplateColumns: "25% 1fr 16.6667%",
        },
        [theme.mobilePortrait]: { display: "block" },
      }}
    >
      {/* This wrapper div prevents Safari from setting the image to 100% the viewport height */}
      <div css={{ position: "relative" }}>
        <SanityImage
          {...image}
          width={media.mobile ? 354 : 180}
          height={media.desktop ? 250 : media.tablet ? 190 : 204}
          css={{
            display: "block",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            [theme.tabletSmall]: {
              position: "absolute",
            },
            [theme.mobilePortrait]: {
              position: "static",
            },
          }}
        />
      </div>
      <div
        css={{
          padding: "3.96vw 3.75vw",
          [theme.tabletSmall]: { padding: "4.69vw 4.95vw" },
          [theme.mobilePortrait]: { padding: 30 },
        }}
      >
        <div
          css={{
            ...theme.t5sm,
            marginBottom: 10,
            color: "var(--labelColor)",
            transition: "color 250ms",
          }}
        >
          {label}
        </div>

        <div css={theme.t4}>{title}</div>
        <div
          css={{
            ...theme.previewText,
            marginTop: 20,
          }}
        >
          {description}
        </div>

        <ReadMoreButton />
      </div>

      <div
        css={{
          display: "none",
          [theme.tabletSmall]: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          },
          [theme.mobilePortrait]: {
            display: "none",
          },
        }}
      >
        <RightArrow
          css={{
            width: 36,
            transition: "color 250ms",
            color: "var(--accentColor)",
          }}
        />
      </div>
    </Link>
  )
}

const ExploreMore = ({ id, links }) => {
  const tablet = useMediaQuery(theme.tabletSmall)
  const mobile = useMediaQuery(theme.mobilePortrait)
  const media = { tablet, mobile, desktop: !mobile && !tablet }

  return (
    <div
      id={id}
      css={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gridGap: 10,
        padding: 10,
        [theme.tabletSmall]: { gridTemplateColumns: "1fr" },
      }}
    >
      {links.map((link, index) => (
        <ExploreMoreItem key={index} {...link} media={media} />
      ))}
    </div>
  )
}

export default ExploreMore

const itemProps = {
  image: PropTypes.shape({
    hotspot: PropTypes.object,
    crop: PropTypes.object,
    asset: PropTypes.object.isRequired,
  }).isRequired,
  label: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  link: PropTypes.string.isRequired,
  media: PropTypes.shape({
    mobile: PropTypes.bool,
    tablet: PropTypes.bool,
    desktop: PropTypes.bool,
  }),
}

ExploreMoreItem.propTypes = itemProps

ExploreMore.propTypes = {
  id: PropTypes.string.isRequired,
  links: PropTypes.arrayOf(PropTypes.shape(itemProps)),
}
